/**
 * Round Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * Supports two shapes: round (full circle) and semi-round (half circle)
 * Pricing: bounding-box formula (D×D) — foam & fabric cut from rectangle
 * SVG: perfect circle top face, side band below
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    extraCovers: { '0': 0, '1': 30, '2': 50 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: 'round',
    diameter: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers (same as RV) ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bounding-box: foam & fabric cut from rectangle) ─── */
  function calcRoundPrice(D, T, shape) {
    if (!D || !T) return null;
    var Dm = D / 39.37, Tm = T / 39.37;
    var boxW = Dm, boxH = (shape === 'semi_round') ? Dm / 2 : Dm;
    var fabricArea = 2 * boxW * boxH + 2 * (boxW + boxH) * Tm;
    var foamVolume = boxW * boxH * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * FABRIC_PRICE_RMB + foamVolume * FOAM_PRICE_RMB
      + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / TARGET_COST_RATIO * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + (ADDON.extraCovers[state.extraCovers] || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() { return state.diameter >= 6 && state.thickness >= 1; }

  function isComplete() {
    return hasDims() && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Perfect circle cylinder (round) / half-cylinder (semi-round)
     Direction vectors: [1,0] and [0,-1] → perfect circle on screen
     viewBox 440×280, center [210,110]
     Side band visible on bottom half: sin(θ) < 0
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var D = state.diameter, T = state.thickness;
    var empty = (!D && !T);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        if ((sc[0] + sc[1] + sc[2]) / 3 > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="rnd-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Geometry ── */
    var isSemi = (state.shape === 'semi_round');
    var radiusPx, thickPx;
    if (empty) {
      radiusPx = 95; thickPx = 20;
    } else {
      var uD = D || 16, uT = T || 3;
      radiusPx = clamp(uD * 3, 30, 125);
      thickPx = clamp(uT * 6, 8, 50);
    }

    var cx = 210, cy = isSemi ? 130 : 110;

    /* ── Generate points ── */
    var N = 72;
    var topPts = [], botPts = [];
    if (isSemi) {
      for (var i = 0; i <= N; i++) {
        var theta = Math.PI + (i / N) * Math.PI;
        var ex = cx + radiusPx * Math.cos(theta);
        var ey = cy + radiusPx * Math.sin(theta);
        topPts.push([ex, ey]);
        botPts.push([ex, ey + thickPx]);
      }
    } else {
      for (var i = 0; i < N; i++) {
        var theta = (i / N) * 2 * Math.PI;
        topPts.push([cx + radiusPx * Math.cos(theta), cy + radiusPx * Math.sin(theta)]);
        botPts.push([cx + radiusPx * Math.cos(theta), cy + radiusPx * Math.sin(theta) + thickPx]);
      }
    }

    /* ── Visible side indices (bottom-facing arc) ── */
    var visIdx = [];
    if (isSemi) {
      for (var i = 0; i <= N; i++) {
        var theta = Math.PI + (i / N) * Math.PI;
        if (Math.sin(theta) > 0) visIdx.push(i);
      }
    } else {
      for (var i = 0; i < N; i++) {
        var theta = (i / N) * 2 * Math.PI;
        if (Math.sin(theta) > 0) visIdx.push(i);
      }
    }

    /* ══ Draw — painter's algorithm: far → near ══ */

    /* ── Straps ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty && visIdx.length > 0) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;
      function drawTie(bx, by, dir) {
        var spread = 4;
        h += '<path d="M' + f(bx - spread * 0.5) + ' ' + f(by)
          + ' Q' + f(bx - spread + dir * 3) + ' ' + f(by + hangLen * 0.5)
          + ' ' + f(bx - spread * 1.5 + dir * 5) + ' ' + f(by + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(bx + spread * 0.5) + ' ' + f(by)
          + ' Q' + f(bx + spread + dir * 2) + ' ' + f(by + hangLen * 0.5)
          + ' ' + f(bx + spread * 1.5 + dir * 4) + ' ' + f(by + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      var midI = visIdx[Math.floor(visIdx.length / 2)];
      drawTie(botPts[midI][0], botPts[midI][1], 0);
      if (state.straps === 'four') {
        var q1 = visIdx[Math.floor(visIdx.length * 0.25)];
        var q3 = visIdx[Math.floor(visIdx.length * 0.75)];
        drawTie(botPts[q1][0], botPts[q1][1], -1);
        drawTie(botPts[q3][0], botPts[q3][1], 1);
      }
    }

    /* ── Semi-round: flat back side band (behind the curved band) ── */
    if (isSemi && thickPx > 0) {
      var flatLeft = topPts[0], flatRight = topPts[topPts.length - 1];
      var flatPath = 'M ' + pt(flatLeft) + ' L ' + pt(flatRight)
        + ' L ' + pt(botPts[botPts.length - 1]) + ' L ' + pt(botPts[0]) + ' Z';
      h += '<path d="' + flatPath + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    }

    /* ── Side band (curved arc) ── */
    if (visIdx.length > 0) {
      var sidePath = 'M ' + pt(topPts[visIdx[0]]);
      for (var i = 1; i < visIdx.length; i++) sidePath += ' L ' + pt(topPts[visIdx[i]]);
      for (var i = visIdx.length - 1; i >= 0; i--) sidePath += ' L ' + pt(botPts[visIdx[i]]);
      sidePath += ' Z';
      h += '<path d="' + sidePath + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

      var botEdge = 'M ' + pt(botPts[visIdx[0]]);
      for (var i = 1; i < visIdx.length; i++) botEdge += ' L ' + pt(botPts[visIdx[i]]);
      h += '<path d="' + botEdge + '" fill="none" stroke="' + strokeC + '" stroke-width="1.4"/>';
    }

    /* ── Top face (drawn last = nearest) ── */
    var topPath = 'M ' + pt(topPts[0]);
    for (var i = 1; i < topPts.length; i++) topPath += ' L ' + pt(topPts[i]);
    topPath += ' Z';
    h += '<path d="' + topPath + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      h += '<path d="' + topPath + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" opacity="0.85"/>';
      if (state.piping === 'double' && visIdx.length > 0) {
        var botPip = 'M ' + pt(botPts[visIdx[0]]);
        for (var i = 1; i < visIdx.length; i++) botPip += ' L ' + pt(botPts[visIdx[i]]);
        h += '<path d="' + botPip + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip ── */
    if (state.antislip && state.antislip !== 'none' && !empty && visIdx.length > 0) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var stripH = 3;
      var stripPath = 'M ' + pt(botPts[visIdx[0]]);
      for (var i = 1; i < visIdx.length; i++) stripPath += ' L ' + pt(botPts[visIdx[i]]);
      for (var i = visIdx.length - 1; i >= 0; i--) {
        var bp = botPts[visIdx[i]];
        stripPath += ' L ' + pt([bp[0], bp[1] + stripH]);
      }
      stripPath += ' Z';
      h += '<path d="' + stripPath + '" fill="' + padC + '" opacity="' + padOp + '"/>';
    }

    /* ── Dimension labels ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = 10, hug = 18;

    /* Diameter — horizontal arrow */
    var dA = [cx - radiusPx, cy];
    var dB = [cx + radiusPx, cy];
    var da1, da2, diaLabelY;
    if (isSemi) {
      da1 = [dA[0], dA[1] + thickPx + hug];
      da2 = [dB[0], dB[1] + thickPx + hug];
    } else {
      da1 = [dA[0], dA[1] + hug];
      da2 = [dB[0], dB[1] + hug];
    }

    h += '<line x1="' + f(dA[0]) + '" y1="' + f(dA[1]) + '" x2="' + f(da1[0]) + '" y2="' + f(da1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(dB[0]) + '" y1="' + f(dB[1]) + '" x2="' + f(da2[0]) + '" y2="' + f(da2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(da1[0]) + '" y1="' + f(da1[1]) + '" x2="' + f(da2[0]) + '" y2="' + f(da2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#rnd-arr)" marker-end="url(#rnd-arr)"/>';

    var diaLabel = D ? 'Ø ' + D + '″' : 'Diameter';
    var dlMx = (da1[0] + da2[0]) / 2, dlMy = (da1[1] + da2[1]) / 2;
    diaLabelY = dlMy + 14;
    h += '<text x="' + f(dlMx) + '" y="' + f(diaLabelY) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">' + diaLabel + '</text>';

    /* Thickness — vertical arrow on right side where side band is visible */
    var thkPt, thkBt;
    if (isSemi) {
      thkPt = topPts[topPts.length - 1];
      thkBt = botPts[botPts.length - 1];
    } else {
      thkPt = topPts[0];
      thkBt = botPts[0];
    }
    h += '<line x1="' + f(thkPt[0]) + '" y1="' + f(thkPt[1]) + '" x2="' + f(thkPt[0] + hug) + '" y2="' + f(thkPt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkBt[0]) + '" y1="' + f(thkBt[1]) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkPt[0] + hug) + '" y1="' + f(thkPt[1] + 2) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1] - 2)
      + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#rnd-arr)" marker-end="url(#rnd-arr)"/>';

    var thkLabel = T ? 'T — ' + T + '″' : 'Thickness';
    h += '<text x="' + f(thkPt[0] + hug + 5) + '" y="' + f((thkPt[1] + thkBt[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">' + thkLabel + '</text>';

    /* ── Empty state hint ── */
    if (empty) {
      var botMostY = 0;
      for (var i = 0; i < visIdx.length; i++) {
        if (botPts[visIdx[i]][1] > botMostY) botMostY = botPts[visIdx[i]][1];
      }
      h += '<text x="' + f(cx) + '" y="' + f(botMostY + 38) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="500" fill="#888">Enter dimensions to preview your cushion</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.diameter && state.thickness)
      parts.push('Ø' + state.diameter + '″ × ' + state.thickness + '″ thick');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    if (state.shape === 'semi_round') parts.unshift('Semi Round');
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your round cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.diameter  = parseFloat(els.dimD.value) || 0;
    state.thickness = parseFloat(els.dimT.value) || 0;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    ['dimD', 'dimT'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var dimsOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    var price = dimsOk ? calcRoundPrice(state.diameter, state.thickness, state.shape) : null;
    var addonTotal = getAddonTotal();

    if (price !== null) {
      var finalPrice = Math.round(price + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(price)];
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ADDON.extraCovers[ec])          parts.push('Covers +$' + ADDON.extraCovers[ec]);
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Throw Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Pillow Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && price !== null) {
        btn.textContent = 'Add to order — $' + Math.round(price + addonTotal) + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!dimsOk) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: price !== null ? Math.round(price + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var price = calcRoundPrice(state.diameter, state.thickness, state.shape);
    var finalPrice = Math.round(price + getAddonTotal());
    var props = {
      'Shape': state.shape === 'semi_round' ? 'Semi Round' : 'Round',
      'Diameter': state.diameter + '"',
      'Thickness': state.thickness + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s)';
    if (state.throwPillow !== 'none') props['Throw Pillow'] = state.throwPillow.replace(/_/g, ' ');
    if (state.pillowCovers !== 'none') props['Pillow Covers'] = state.pillowCovers.replace(/_/g, ' ');
    if (state.armrestPillow !== 'none') props['Armrest Pillow'] = state.armrestPillow.replace(/_/g, ' ');
    if (state.zipperPlacement) props['Zipper Placement'] = state.zipperPlacement;
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    var apiPayload = {
      shape: state.shape,
      pieces: [{ diameter: state.diameter, thickness: state.thickness }],
      piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow,
      pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };

    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── DOM transformation (adapt shared Liquid section for Round) ─── */
  function transformDOM() {
    /* Hide back dimension inputs + label */
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) backDims[i].style.display = 'none';
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.style.display = 'none';

    /* Hide seat label (single piece, no need for "SEAT" heading) */
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) seatLabel.style.display = 'none';

    /* Relabel L → Diameter */
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.textContent = 'Diameter (″)';

    /* Hide W input — find its field wrapper */
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) {
      var wWrap = inputW.closest('.cc-dim-field') || inputW.closest('.cc-sp-dim-field') || inputW.parentElement;
      if (wWrap) wWrap.style.display = 'none';
    }

    /* Adjust seat dims grid to 2 columns (was 3) */
    var dimsGrid = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (dimsGrid) dimsGrid.style.gridTemplateColumns = '1fr 1fr';

    /* Relabel T */
    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.textContent = 'Thickness (″)';

    /* Replace shape grid with round + semi-round options */
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.innerHTML = ''
        + '<div class="cc-opt-card sel" data-group="shape" data-value="round">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/round_cushion.webp?v=1783438200" alt="Round"></div>'
        +   '<div class="cc-opt-name">Round</div><div class="cc-opt-price">included</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="shape" data-value="semi_round">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/semi_round_cushion.webp?v=1783438199" alt="Semi Round"></div>'
        +   '<div class="cc-opt-name">Semi Round</div><div class="cc-opt-price">included</div>'
        + '</div>';
    }

    /* Adjust SVG viewBox for round shape */
    var svg = document.getElementById('cc-dim-svg');
    if (svg) {
      svg.setAttribute('viewBox', '0 0 440 280');
      svg.style.maxHeight = '200px';
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.dimD         = document.getElementById('cc-dim-l');
    els.dimT         = document.getElementById('cc-dim-t');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['dimD', 'dimT'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    if (els.notes) els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.RoundCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
